/**
 * "In deinem Raum ansehen" — opens the selected colour's 3D model in the
 * phone's own AR viewer. Wired to the gallery's corner button
 * (blocks/_kc-product-detail-left-side.liquid, view_3d_ar).
 *
 * Three cases, decided at click time so a variant re-render can never leave a
 * stale handler behind — every model URL is read from the element itself:
 *   - iPhone/iPad: the button IS the Quick Look link (<a rel="ar"> with an
 *     <img> child and a .usdz href), so it works even before this script runs.
 *     Nothing to do here.
 *   - Android: <model-viewer> (loaded through Shopify, as the theme's own 3D
 *     media does), which opens AR in Chrome itself and only falls back to
 *     Scene Viewer. Until it has loaded: a Scene Viewer intent: URL built from
 *     the .glb, with the product page as the browser fallback.
 *   - Everything else (desktop, and in-app browsers that cannot do AR): a QR
 *     code of this exact colour's page, drawn in the page itself by the
 *     vendored kc-qrcode.min.js — no third-party request.
 *
 * Scanning that QR opens the page with ?ar=1, which launches AR straight away on an iPhone.
 */

const UA = navigator.userAgent;
const IS_IOS = /iPad|iPhone|iPod/.test(UA) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
const IS_ANDROID = /Android/.test(UA);
// Browsers inside apps cannot hand over to the AR viewer.
const IN_APP = /FBAN|FBAV|Instagram|TikTok|Line\/|Pinterest|Snapchat/.test(UA);

let libPromise = null;
let dialog = null;

function loadQrLib(src) {
  if (libPromise) return libPromise;
  libPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
  return libPromise;
}

function sceneViewerUrl(glb, fallback) {
  return (
    "intent://arvr.google.com/scene-viewer/1.0?file=" +
    encodeURIComponent(glb) +
    "&mode=ar_preferred&resizable=false&title=" +
    encodeURIComponent(document.title) +
    "#Intent;scheme=https;package=com.google.ar.core;action=android.intent.action.VIEW;" +
    "S.browser_fallback_url=" +
    encodeURIComponent(fallback) +
    ";end;"
  );
}

/*
 * Android opens AR the way Google's own demo and Shopify's 3D media do: <model-viewer> starts it in Chrome
 * (WebXR; the customer is asked once to allow it) and falls back to Scene Viewer. The bare intent above opened
 * and bounced straight back to the page on phones where Scene Viewer does not start (tested on a real phone).
 * Loaded only on Android, only on a page with a model, after the page has loaded.
 */
let viewer = null;

// Made as soon as the library is there, so it has picked its AR mode before the tap (picking takes a moment,
// and activateAR() before that does nothing). Off screen, it downloads no model until AR starts; in AR, Chrome
// shows it full screen by itself (WebXR DOM overlay).
function createViewer() {
  if (viewer || !customElements.get("model-viewer")) return;
  const first = document.querySelector("[data-kc-ar][data-ar-glb]");
  viewer = document.createElement("model-viewer");
  viewer.setAttribute("ar", "");
  viewer.setAttribute("ar-modes", "webxr scene-viewer");
  viewer.setAttribute("ar-scale", "fixed");
  viewer.style.cssText = "position:fixed;left:-9999px;top:0;width:1px;height:1px;";
  if (first) viewer.setAttribute("src", first.dataset.arGlb);
  document.body.appendChild(viewer);
}

function loadViewer() {
  if (!window.Shopify || !window.Shopify.loadFeatures) return;
  // Shopify's onLoad can come before the element is registered: wait for the registration itself.
  customElements.whenDefined("model-viewer").then(createViewer);
  window.Shopify.loadFeatures([{ name: "model-viewer", version: "1.12", onLoad: () => {} }]);
}

function openInViewer(glb) {
  if (viewer.getAttribute("src") !== glb) viewer.setAttribute("src", glb);
  viewer.activateAR();
}

if (IS_ANDROID && !IN_APP && document.querySelector("[data-kc-ar][data-ar-glb]")) {
  const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1500));
  if (document.readyState === "complete") idle(loadViewer);
  else window.addEventListener("load", () => idle(loadViewer), { once: true });
}

/*
 * Android: the link itself must never point at the iPhone file. Its href is the .usdz (Quick Look needs that on
 * iOS), and on real Android phones taps that never reached the click handler below downloaded that .usdz. So on
 * Android the href becomes the Scene Viewer hand-off with the .glb: a tap opens AR even without this script's
 * handler. Set on load, again after a variant change rebuilds the gallery, and right before every tap.
 */
function androidHref(link) {
  if (link.tagName !== "A" || !link.dataset.arGlb) return;
  link.setAttribute("href", sceneViewerUrl(link.dataset.arGlb, pageUrl(link, false)));
  link.removeAttribute("rel");
}

if (IS_ANDROID && !IN_APP) {
  const fixLinks = () => document.querySelectorAll("a[data-kc-ar][data-ar-glb]").forEach(androidHref);
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", fixLinks, { once: true });
  else fixLinks();
  document.addEventListener("product:rerender", () => setTimeout(fixLinks, 0));
  document.addEventListener(
    "pointerdown",
    (event) => {
      const link = event.target.closest("a[data-kc-ar]");
      if (link) androidHref(link);
    },
    true
  );
}

function ensureDialog() {
  if (dialog && document.body.contains(dialog)) return dialog;
  dialog = document.createElement("div");
  dialog.className = "kc-ar-qr";
  dialog.hidden = true;
  dialog.innerHTML =
    '<div class="kc-ar-qr__backdrop" data-ar-qr-close></div>' +
    '<div class="kc-ar-qr__panel" role="dialog" aria-modal="true">' +
    '<button type="button" class="kc-ar-qr__close" data-ar-qr-close aria-label="Schließen">&times;</button>' +
    '<p class="kc-ar-qr__title"></p>' +
    '<div class="kc-ar-qr__code"></div>' +
    '<p class="kc-ar-qr__hint"></p>' +
    "</div>";
  dialog.addEventListener("click", (event) => {
    if (event.target.closest("[data-ar-qr-close]")) closeDialog();
  });
  document.body.appendChild(dialog);
  return dialog;
}

function closeDialog() {
  if (dialog) dialog.hidden = true;
  document.removeEventListener("keydown", onKey);
}

function onKey(event) {
  if (event.key === "Escape") closeDialog();
}

/**
 * The page this colour lives on, built from the page actually open so a theme
 * preview stays a theme preview when the code is scanned. `ar` launches AR on
 * arrival; the Android fallback deliberately omits it, or a phone without
 * Scene Viewer would bounce between page and viewer.
 */
function pageUrl(trigger, withAr) {
  let url;
  try {
    url = new URL(window.location.href);
  } catch (e) {
    return trigger.dataset.arPage || window.location.href;
  }
  if (trigger.dataset.arVariant) url.searchParams.set("variant", trigger.dataset.arVariant);
  // On a theme preview Shopify drops preview_theme_id from the address after
  // the first load; put it back, or the phone would open the live theme.
  const theme = window.Shopify && window.Shopify.theme;
  if (theme && theme.role && theme.role !== "main" && theme.id) url.searchParams.set("preview_theme_id", String(theme.id));
  if (withAr) url.searchParams.set("ar", "1");
  else url.searchParams.delete("ar");
  return url.toString();
}

async function openQr(trigger) {
  const page = pageUrl(trigger, true);
  if (!page) return;
  const box = ensureDialog();
  box.querySelector(".kc-ar-qr__title").textContent = trigger.dataset.arQrTitle || "";
  box.querySelector(".kc-ar-qr__hint").textContent = trigger.dataset.arQrHint || "";
  const holder = box.querySelector(".kc-ar-qr__code");
  holder.textContent = "";
  box.hidden = false;
  document.addEventListener("keydown", onKey);
  try {
    await loadQrLib(trigger.dataset.qrLib);
    /* global QRCode */
    new QRCode(holder, {
      text: page,
      width: 200,
      height: 200,
      correctLevel: QRCode.CorrectLevel.M,
    });
  } catch (e) {
    // Without the library there is still something to act on.
    const link = document.createElement("a");
    link.href = page;
    link.textContent = page;
    holder.appendChild(link);
  }
}

// Capture phase: runs before the gallery's own handlers, so none of them can stop the tap reaching it.
document.addEventListener("click", (event) => {
  const trigger = event.target.closest("[data-kc-ar]");
  if (!trigger) return;

  // iOS: the anchor is the Quick Look link — let the browser have it.
  if (IS_IOS && !IN_APP && trigger.tagName === "A") return;

  event.preventDefault();

  const glb = trigger.dataset.arGlb;
  if (IS_ANDROID && !IN_APP && glb) {
    createViewer();
    if (viewer && viewer.canActivateAR) openInViewer(glb);
    else window.location.href = sceneViewerUrl(glb, pageUrl(trigger, false));
    return;
  }

  openQr(trigger);
}, true);

// Arriving from the QR on an iPhone: open AR straight away, once. Not on Android: AR in Chrome needs the
// customer's own tap, and an untapped hand-off only bounced back to the page.
if (/[?&]ar=1/.test(window.location.search) && IS_IOS && !IN_APP) {
  const start = () => {
    const link = document.querySelector("a[data-kc-ar][href]");
    if (link) setTimeout(() => link.click(), 600);
  };
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start, { once: true });
  else start();
}
